import { useEffect, useMemo, useState } from "react";
import { Check, AlertCircle, ArrowRight, MapPin } from "lucide-react";
import { STATES, ELIGIBLE_COUNT, STATE_BY_CODE, type State } from "@/config/states";
import { INTAKE_URLS } from "@/config/intake";
import { joinWaitlist } from "@/lib/waitlist";

const STORAGE_KEY = "olive.state.v1";

export const StateEligibility = () => {
  const [code, setCode] = useState<string>("");
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    try {
      const v = localStorage.getItem(STORAGE_KEY);
      if (v && STATE_BY_CODE[v]) setCode(v);
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    if (code) {
      try { localStorage.setItem(STORAGE_KEY, code); } catch { /* ignore */ }
    }
  }, [code]);

  const state: State | undefined = code ? STATE_BY_CODE[code] : undefined;
  const intakeHref = useMemo(
    () => state ? `${INTAKE_URLS.default}?state=${state.code}` : INTAKE_URLS.default,
    [state]
  );

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null);
    if (!state) return;
    const res = await joinWaitlist({ email, state: state.code });
    if (res.ok) setSubmitted(true);
    else setErr("error" in res ? res.error : "Could not save.");
  };

  return (
    <div className="mt-8 rounded-2xl border border-border bg-card/80 backdrop-blur p-4 md:p-5">
      <label htmlFor="state-eligibility" className="flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-foreground/55 font-medium mb-3">
        <MapPin className="size-3.5 text-accent" aria-hidden />
        Available in {ELIGIBLE_COUNT} states — check yours
      </label>

      <div className="flex flex-col sm:flex-row gap-2.5">
        <div className="relative flex-1">
          <select
            id="state-eligibility"
            value={code}
            onChange={(e) => { setCode(e.target.value); setSubmitted(false); setErr(null); }}
            className="w-full appearance-none rounded-full border border-border bg-background px-4 py-3 text-sm text-foreground pr-9 focus-visible:outline-2 focus-visible:outline-accent focus-visible:outline-offset-2"
            aria-label="Select your state"
          >
            <option value="" disabled>Select your state</option>
            {STATES.map((s) => (
              <option key={s.code} value={s.code}>{s.name}</option>
            ))}
          </select>
          <span aria-hidden className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-foreground/40 text-xs">▾</span>
        </div>

        {state?.eligible && (
          <a
            href={intakeHref}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 rounded-full bg-primary text-primary-foreground px-5 py-3 text-sm font-medium hover:opacity-90 transition focus-visible:outline-2 focus-visible:outline-accent focus-visible:outline-offset-2"
          >
            Continue
            <ArrowRight className="size-4" />
          </a>
        )}
      </div>

      {state?.eligible && (
        <p className="mt-3 inline-flex items-center gap-1.5 text-xs text-foreground/70">
          <Check className="size-3.5 text-accent" strokeWidth={2.5} />
          You're in. Olive is live in {state.name}.
        </p>
      )}

      {state && !state.eligible && !submitted && (
        <form onSubmit={onSubmit} className="mt-3.5 space-y-2.5">
          <p className="inline-flex items-start gap-1.5 text-xs text-foreground/70">
            <AlertCircle className="size-3.5 text-accent mt-0.5 shrink-0" />
            We're not live in {state.name} yet — leave your email and we'll let you know.
          </p>
          <div className="flex flex-col sm:flex-row gap-2.5">
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@email.com"
              className="flex-1 rounded-full border border-border bg-background px-4 py-3 text-sm focus-visible:outline-2 focus-visible:outline-accent focus-visible:outline-offset-2"
            />
            <button
              type="submit"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-primary text-primary-foreground px-5 py-3 text-sm font-medium hover:opacity-90 transition"
            >
              Notify me
            </button>
          </div>
          {err && <p className="text-xs text-destructive">{err}</p>}
        </form>
      )}

      {submitted && (
        <p className="mt-3 inline-flex items-center gap-1.5 text-xs text-foreground/70">
          <Check className="size-3.5 text-accent" strokeWidth={2.5} />
          Got it — we'll email you the moment {state?.name} opens up.
        </p>
      )}
    </div>
  );
};
